// ============================================================
// dev-core-web/src/lib/queryClient.ts
// Version: 1.1.0
// Purpose:
//   - централизованный QueryClient для React Query
//   - единые правила retry / staleTime / gcTime / refetch
//   - классификация ошибок (retryable / non-retryable)
//   - НЕ содержит бизнес-логики, НЕ делает RBAC/Classification решений
//
// Ссылки:
//   - docs/API.md §6.4, §7, §16.1, §17.2, §18
//   - docs/COPILOT.md §16.2
//   - docs/MONITORING.md §24
//   - docs/adr/0002-envelope-vs-kms.md (cache not authority)
// ============================================================

import {
  QueryClient,
  type DefaultOptions,
} from '@tanstack/react-query';
import { ApiError } from '@/lib/api';

// ------------------------------------------------------------
// Retry policy
// ------------------------------------------------------------
//
// API.md §17.2: retryable=true — не значит «повторять сразу».
// API.md §7: 4xx — клиентские ошибки (не ретраить),
//            5xx — серверные (можно ретраить с бэкоффом).
// API.md §13.2: 401 — не ретраить (сессия истекла).
// API.md §16.1: 429 — ретраить, но учитывая Retry-After.
//
// Fail-closed: если не знаем, что за ошибка — не ретраим
// (лучше показать пользователю, чем устроить шторм).

const MAX_RETRIES = 2;

// 4xx коды, которые ОДНОЗНАЧНО нет смысла ретраить
// (не зависит от транзиентности сервера).
const NON_RETRYABLE_STATUS = new Set<number>([
  400, // INVALID_INPUT / VALIDATION_FAILED / INVALID_QUERY
  401, // UNAUTHENTICATED — нужен новый логин
  403, // PERMISSION_DENIED / SCOPE_REQUIRED / CLASSIFICATION_DENIED
  404, // NOT_FOUND
  409, // CONFLICT / IDEMPOTENCY_KEY_REUSED / RESOURCE_VERSION_CONFLICT
  410, // GONE
  412, // PRECONDITION_REQUIRED (ретраить бессмысленно без изменения)
  413, // PAYLOAD_TOO_LARGE
  415, // UNSUPPORTED_MEDIA_TYPE
  422, // VALIDATION_FAILED (сервер отклонил данные)
  428, // PRECONDITION_REQUIRED (семантика API.md §10)
]);

// Коды ошибок (API.md §17.1), которые однозначно не ретраить,
// даже если HTTP status не попал в NON_RETRYABLE_STATUS.
// Например, 429 — ретраить можно, но RATE_LIMITED с длинным
// Retry-After может быть отложен; логика ниже учитывает это.
const NON_RETRYABLE_CODES = new Set<string>([
  // Auth / access
  'UNAUTHENTICATED',
  'PERMISSION_DENIED',
  'SCOPE_REQUIRED',
  'CLASSIFICATION_DENIED',
  // Input / validation
  'INVALID_INPUT',
  'VALIDATION_FAILED',
  'INVALID_QUERY',
  // Resource state
  'NOT_FOUND',
  'CONFLICT',
  'DUPLICATE',
  'GONE',
  'PRECONDITION_REQUIRED',
  'RESOURCE_VERSION_CONFLICT',
  'IDEMPOTENCY_KEY_REUSED',
  'IDEMPOTENCY_KEY_MISSING',
  // Payload
  'PAYLOAD_TOO_LARGE',
  'UNSUPPORTED_MEDIA_TYPE',
  // Actions
  'ACTION_NOT_FOUND',
  'APPROVAL_REQUIRED',
  'APPROVAL_DENIED',
  'APPROVAL_EXPIRED',
  'INVALID_EXECUTION_TRANSITION',
  'ACTION_ABORTED',
  'ACTION_CANCELLED',
  'ACTION_FAILED',
  'SNAPSHOT_FAILED',
  'ROLLBACK_FAILED',
  // Copilot (COPILOT.md §18.4)
  'COPILOT_BUDGET_EXCEEDED',
  'COPILOT_POLICY_VIOLATED',
  'COPILOT_TOOL_DENIED',
  'COPILOT_OUTPUT_BLOCKED',
  'COPILOT_SESSION_LIMIT',
  // Клиентские 4xx без явного кода
  'IDEMPOTENCY_KEY_REUSED',
]);

function isRetryableError(error: unknown): boolean {
  // ApiError — типизированная ошибка из lib/api.ts
  if (error instanceof ApiError) {
    // 1. Явный non-retryable code побеждает
    if (NON_RETRYABLE_CODES.has(error.code)) return false;

    // 2. Явный non-retryable status
    if (error.status > 0 && NON_RETRYABLE_STATUS.has(error.status)) return false;

    // 3. 429 RATE_LIMITED — ретраить, но с уважением к Retry-After
    // (см. retryDelay ниже). Возвращаем true.
    if (error.status === 429) return true;

    // 4. 5xx — серверная сторона, ретраить
    if (error.status >= 500 && error.status < 600) return true;

    // 5. Сетевой сбой (status === 0) — ретраить
    if (error.status === 0) return true;

    // 6. Явный флаг retryable из envelope
    if (error.retryable === true) return true;

    // 7. Fail-closed: неизвестная ApiError — не ретраить
    return false;
  }

  // Не-ApiError: неизвестный класс ошибки. Не ретраим.
  // Это защищает от случайного retry-шторма на программерских
  // ошибках (TypeError, ReferenceError и т.п.).
  return false;
}

// ------------------------------------------------------------
// Retry delay
// ------------------------------------------------------------
//
// Экспоненциальный backoff с jitter. Учитывает Retry-After
// для 429, если сервер его прислал (API.md §3.3).
//
// Не используем arbitrary numbers без имени.

const BASE_DELAY_MS = 500;
const MAX_DELAY_MS = 10_000;

function retryDelay(attempt: number, error: unknown): number {
  // Retry-After — API.md §16.1, §17.2.
  // Если сервер прислал заголовок, уважаем его (в секундах).
  const retryAfterSeconds = extractRetryAfter(error);
  if (retryAfterSeconds !== null) {
    return Math.min(retryAfterSeconds * 1000, MAX_DELAY_MS);
  }

  // Экспоненциальный backoff: base * 2^attempt, capped.
  const exp = Math.min(BASE_DELAY_MS * 2 ** attempt, MAX_DELAY_MS);

  // Jitter ±20% — снижает thundering herd.
  const jitter = 0.8 + Math.random() * 0.4;
  return Math.round(exp * jitter);
}

function extractRetryAfter(error: unknown): number | null {
  if (!(error instanceof ApiError)) return null;

  // details.retryAfter — конвенция из envelope (API.md §6.4 details).
  const details = error.details;
  if (!details || typeof details !== 'object') return null;

  const value = (details as Record<string, unknown>).retryAfter;
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    return value;
  }
  return null;
}

// ------------------------------------------------------------
// Обработка 401 (UNAUTHENTICATED)
// ------------------------------------------------------------
//
// При 401 — сессия истекла. Не ретраим (уже в
// NON_RETRYABLE_CODES), но нужно оповестить приложение.
// Обработчик регистрируется через setUnauthorizedHandler,
// чтобы не создавать циклическую зависимость queryClient ↔
// authStore ↔ App.

type UnauthorizedHandler = () => void;

let onUnauthorized: UnauthorizedHandler | null = null;

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  onUnauthorized = handler;
}

export function handleQueryError(error: unknown): void {
  if (error instanceof ApiError && error.code === 'UNAUTHENTICATED') {
    onUnauthorized?.();
  }
}

// ------------------------------------------------------------
// Default options
// ------------------------------------------------------------
//
// staleTime / gcTime — согласованы с реальностью:
//   - большинство ресурсов меняются нечасто;
//   - но при realtime-событиях UI должен инвалидировать
//     кэш через queryClient.invalidateQueries.
//   - API.md §13 realtime — источник свежести.
//
// Defaults НЕ ЗАМЕНЯЮТ явные опции в каждом useQuery:
// critical paths (copilot, actions) ставят свои.

const DEFAULTS: DefaultOptions = {
  queries: {
    // 30 секунд — ок для read-heavy ресурсов.
    // Инвалидируется через WebSocket-события (lib/notifications.ts).
    staleTime: 30_000,

    // 5 минут — кэш удерживается после того, как компонент размонтирован.
    gcTime: 5 * 60_000,

    // Refetch при возврате фокуса — включено осторожно:
    // это часть UX для refresh-heavy операционных экранов.
    // Для конкретных страниц можно выключить опцией.
    refetchOnWindowFocus: true,

    // Refetch при reconnect (сеть вернулась) — важно для
    // realtime-контуров.
    refetchOnReconnect: true,

    // Retry — наша централизованная логика.
    retry: (failureCount, error) => {
      if (failureCount >= MAX_RETRIES) return false;
      return isRetryableError(error);
    },
    retryDelay,

    // Не показывать «pending» при фоновом рефетче, если данные
    // уже есть. Иначе UI будет мерцать.
    // (React Query v5: это делает isFetching, не isPending.)
  },

  mutations: {
    // Мутации НЕ ретраим по умолчанию.
    // Actions имеют собственную идемпотентность/retry policy
    // (ACTIONS.md §8.4). Клиентский retry может создать
    // дубликат side effect, если Idempotency-Key не совпал.
    // Если нужен retry — вызывающий код ставит его явно
    // с Idempotency-Key (API.md §11.1).
    retry: false,

    // onError — глобальный хук для обработки 401.
    // Компоненты всё равно обрабатывают ошибки локально
    // через onError/mutateAsync catch.
    onError: (error) => {
      handleQueryError(error);
    },
  },
};

// ------------------------------------------------------------
// Фабрика (на случай SSR/тестов — каждый тест хочет свой клиент)
// ------------------------------------------------------------

// Подписка на глобальные ошибки — нужна любому клиенту,
// который реально используется в приложении (App.tsx держит
// свой QueryClient), иначе 401 из query не доходит до authStore.
export function subscribeUnauthorized(client: QueryClient): void {
  client.getQueryCache().subscribe((event) => {
    if (event.type === 'updated' && event.query.state.status === 'error') {
      handleQueryError(event.query.state.error);
    }
  });
}

export function createQueryClient(): QueryClient {
  const client = new QueryClient({ defaultOptions: DEFAULTS });
  subscribeUnauthorized(client);
  return client;
}

// ------------------------------------------------------------
// Singleton — используется как источник DEFAULTS и queryKeys.
// Провайдер в App.tsx создаёт клиента сам и подписывается
// через subscribeUnauthorized.
// ------------------------------------------------------------

export const queryClient = createQueryClient();

// ------------------------------------------------------------
// Query key helpers
// ------------------------------------------------------------
//
// Единый стиль query-ключей — чтобы invalidate работал
// предсказуемо (React Query матчит по префиксу массива).

export const queryKeys = {
  // Auth
  me: ['auth', 'me'] as const,

  // RBAC
  rbacEffective: ['rbac', 'effective'] as const,
  rbacRoles: ['rbac', 'roles'] as const,
  rbacRole: (id: string) => ['rbac', 'roles', id] as const,
  rbacBindings: ['rbac', 'bindings'] as const,

  // Classification
  classificationClearances: ['classification', 'clearances'] as const,
  classificationPolicies: ['classification', 'policies'] as const,

  // Approval
  approvals: ['approvals'] as const,
  approval: (id: string) => ['approvals', id] as const,

  // Audit
  audit: (filters?: Record<string, unknown>) =>
    filters ? (['audit', filters] as const) : (['audit'] as const),

  // Actions
  actionsHistory: (filters?: Record<string, unknown>) =>
    filters ? (['actions', 'history', filters] as const) : (['actions', 'history'] as const),
  actionExecution: (id: string) => ['actions', 'executions', id] as const,

  // Fleet
  fleetNodes: ['fleet', 'nodes'] as const,
  fleetNode: (id: string) => ['fleet', 'nodes', id] as const,
  fleetClusters: ['fleet', 'clusters'] as const,

  // Inventory
  inventoryEntities: (filters?: object) =>
    filters ? (['inventory', filters] as const) : (['inventory'] as const),
  inventoryEntity: (id: string) => ['inventory', id] as const,

  // Providers / PM2 (PROVIDERS.md §15.1)
  pm2Processes: ['providers', 'pm2', 'processes'] as const,
  containers: ['providers', 'docker', 'containers'] as const,
  systemdServices: ['systemd', 'services'] as const,
  nginxSites: ['providers', 'nginx', 'sites'] as const,
  redisInfo: ['providers', 'redis', 'info'] as const,
  prometheusTargets: ['providers', 'prometheus', 'targets'] as const,
  prometheusQuery: ['providers', 'prometheus', 'query'] as const,
  minioBuckets: ['providers', 'minio', 'buckets'] as const,
  minioObjects: ['providers', 'minio', 'objects'] as const,
  hostPorts: ['providers', 'host', 'ports'] as const,
  hostPortsFree: ['providers', 'host', 'ports', 'free'] as const,
  providersCatalog: ['providers', 'catalog'] as const,
  provider: (key: string) => ['providers', 'catalog', key] as const,

  // Incidents
  incidents: ['incidents'] as const,
  incident: (id: string) => ['incidents', id] as const,

  // Copilot
  copilotSessions: ['copilot', 'sessions'] as const,
  copilotSession: (id: string) => ['copilot', 'sessions', id] as const,
  copilotTools: ['copilot', 'tools'] as const,
  copilotModels: ['copilot', 'models'] as const,

  // Revenue
  revenuePlans: ['revenue', 'plans'] as const,
  revenueSubscriptions: ['revenue', 'subscriptions'] as const,
  revenueInvoices: ['revenue', 'invoices'] as const,
  revenuePayments: ['revenue', 'payments'] as const,

  // Metrics
  metrics: (name: string, filters?: Record<string, unknown>) =>
    filters ? (['metrics', name, filters] as const) : (['metrics', name] as const),

  // Users
  users: ['users'] as const,
  user: (id: string) => ['users', id] as const,
} as const;